import {
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext
} from 'react';

import {
  clientErrorTracker,
  useSoyuzClientConfig,
  useSoyuzFlag,
  useSoyuzLocales
} from '@spotahome/soyuz/client';

const DEFAULT_USE_RECAPTCHA_NET = false;

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function getRecaptchaEnterprise() {
  const g = window?.grecaptcha;
  return g?.enterprise;
}

function createScriptSrc({ siteKey, useRecaptchaNet, language }) {
  const host = useRecaptchaNet
    ? 'https://www.recaptcha.net'
    : 'https://www.google.com';
  const qs = new URLSearchParams({ render: siteKey });

  if (language) {
    qs.set('hl', language);
  }

  return `${host}/recaptcha/enterprise.js?${qs.toString()}`;
}

const ReCaptchaContext = createContext(null);

export function ReCaptchaProvider({
  children,
  nonce,
  useRecaptchaNet = DEFAULT_USE_RECAPTCHA_NET
}) {
  const [error, setError] = useState(null);
  const { recaptchaSiteKey } = useSoyuzClientConfig();
  const { current: language } = useSoyuzLocales();
  const TenantRecaptchaFlag = useSoyuzFlag('TenantRecaptcha');

  useEffect(() => {
    if (!recaptchaSiteKey) {
      setError(
        new Error(
          "Missing reCAPTCHA site key. Pass 'siteKey' to <ReCaptchaProvider />."
        )
      );
    } else {
      setError(null);
    }
  }, [recaptchaSiteKey]);

  const handleScriptError = useCallback(err => {
    setError(
      err instanceof Error
        ? err
        : new Error('Failed to load reCAPTCHA Enterprise script.')
    );
  }, []);

  const executeRecaptcha = useCallback(
    async action => {
      if (!recaptchaSiteKey) {
        throw new Error('Missing reCAPTCHA site key.');
      }

      const api = getRecaptchaEnterprise();

      if (!api?.execute) {
        throw new Error('reCAPTCHA Enterprise API not available.');
      }

      if (!action) {
        throw new Error(
          'Missing reCAPTCHA action. Pass an action to executeRecaptcha(action).'
        );
      }

      return new Promise((resolve, reject) => {
        api.ready(() => {
          api.execute(recaptchaSiteKey, { action }).then(resolve).catch(reject);
        });
      });
    },
    [recaptchaSiteKey]
  );

  const scriptSrc = useMemo(
    () =>
      recaptchaSiteKey
        ? createScriptSrc({
            siteKey: recaptchaSiteKey,
            useRecaptchaNet,
            language
          })
        : '',
    [recaptchaSiteKey, useRecaptchaNet, language]
  );

  const value = useMemo(
    () => ({
      reCaptchaKey: recaptchaSiteKey,
      grecaptcha: isBrowser() ? getRecaptchaEnterprise() : undefined,
      error,
      executeRecaptcha
    }),
    [recaptchaSiteKey, error, executeRecaptcha]
  );

  const apiExists = isBrowser() && getRecaptchaEnterprise() !== undefined;

  return (
    <ReCaptchaContext.Provider value={value}>
      {!apiExists && recaptchaSiteKey && TenantRecaptchaFlag.isOn() && (
        <script
          src={scriptSrc}
          defer
          async
          type="text/javascript"
          onError={handleScriptError}
          nonce={nonce}
        />
      )}

      {children}
    </ReCaptchaContext.Provider>
  );
}

export function useReCaptcha(action) {
  const TenantRecaptchaFlag = useSoyuzFlag('TenantRecaptcha');
  const { executeRecaptcha } = useContext(ReCaptchaContext);

  return {
    executeRecaptcha: async () => {
      if (TenantRecaptchaFlag.isOff()) {
        return null;
      }

      let recaptchaToken = null;

      try {
        recaptchaToken = await executeRecaptcha(action);
      } catch (error) {
        clientErrorTracker.error({
          error,
          source: `ReCaptchaContext-useExecuteRecaptcha-${action}`
        });
      }

      return recaptchaToken;
    }
  };
}

export default ReCaptchaProvider;
